/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';

export enum UserMediaAccessStateEnum {
  pending = 0,
  rejected,
  allowed,
}

export interface IPermissionDevice {
  microphone: boolean;
  camera: boolean;
}

export interface MediaDevicesError {
  name:
    | 'NotAllowedError'
    | 'NotFoundError'
    | 'NotReadableError'
    | 'OverconstrainedError'
    | 'AbortError'
    | 'SecurityError'
    | 'TypeError';
  message: string;
  customMessage: string;
  constraint?: string;
}

const useUserMediaAccessState = () => {
  const [loading, setLoading] = useState(false);

  const [isAccessAllowed, setIsAccessAllowed] = useState(UserMediaAccessStateEnum.pending);
  const [permissionError, setPermissionError] = useState<MediaDevicesError | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [devicePermission, setDevicePermission] = useState<IPermissionDevice>({
    microphone: true,
    camera: true,
  });

  useEffect(() => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error('Access to your local tracks is not supported by browser');
      }

      if (!devicePermission.microphone && !devicePermission.camera) {
        return;
      }

      checkPermission('microphone');
      checkPermission('camera');

      navigator?.mediaDevices?.addEventListener('devicechange', getDevices);

      getDevices();
    } catch (err) {
      console.error(err);
    }

    return () => {
      navigator?.mediaDevices?.removeEventListener('devicechange', getDevices);
    };
  }, []);

  const getDevices = async () => {
    setLoading(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      devices && setDevices(devices);
    } catch (error) {
      // setPermissionError(error);
    } finally {
      setLoading(false);
    }
  };

  const checkPermission = async (type: 'camera' | 'microphone') => {
    try {
      const newType = type === 'camera' ? 'video' : 'audio';
      const res = await navigator.mediaDevices.getUserMedia({
        [newType]: true,
      });
      res.getTracks().forEach(function (track) {
        track.stop();
      });
    } catch (err) {
      if (err.name == 'NotAllowedError') {
        setDevicePermission((prev) => {
          return { ...prev, [type]: false };
        });
        setPermissionError({
          name: 'NotAllowedError',
          message: err.message ? err.message : '',
          customMessage: 'Permission Denied',
        });
      }
      if (err.name == 'NotReadableError') {
        setPermissionError({
          name: 'NotReadableError',
          message: err.message ? err.message : '',
          customMessage: 'Camera or microphone is already in use by another application',
        });
      }
      if (err.name == 'NotFoundError') {
        setPermissionError({
          name: 'NotFoundError',
          message: err.message ? err.message : '',
          customMessage: 'No camera or microphone found',
        });
      }
      if (err.name == 'AbortError') {
        setPermissionError({
          name: 'AbortError',
          message: err.message ? err.message : '',
          customMessage: 'The user has rejected permission',
        });
      }
      if (err.name == 'OverconstrainedError') {
        setPermissionError({
          name: 'OverconstrainedError',
          message: err.message ? err.message : '',
          customMessage: 'Constraints could be not satisfied.',
        });
      }
      throw err;
    }
  };

  useEffect(() => {
    if (navigator.userAgent.search('Firefox')) {
      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: true,
        })
        .then(() => setIsAccessAllowed(UserMediaAccessStateEnum.allowed))
        .catch(() => {
          setIsAccessAllowed(UserMediaAccessStateEnum.rejected);
        });
      return;
    }
    const camera = navigator.permissions.query({ name: 'camera' } as any);
    const microphone = navigator.permissions.query({ name: 'microphone' } as any);

    Promise.all([camera, microphone])
      .then(() => setIsAccessAllowed(UserMediaAccessStateEnum.allowed))
      .catch(() => setIsAccessAllowed(UserMediaAccessStateEnum.rejected));
  }, []);

  return {
    isAccessAllowed,
    audioInputDevices: devices.filter((device) => device.kind === 'audioinput'),
    videoInputDevices: devices.filter((device) => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter((device) => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.filter((device) => device.kind === 'audioinput').length > 0,
    hasVideoInputDevices: devices.filter((device) => device.kind === 'videoinput').length > 0,
    devicePermission,
    permissionError,
    loading,
  };
};

export default useUserMediaAccessState;
